import { type FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useController, useFormContext } from "react-hook-form";
import type { SxProps, Theme } from "@mui/material";
import { Box, FormControl } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { DatePicker } from "../../shared/DatePicker";
import { LockIcon } from "../../shared/icons";
import { useDynamicFormState } from "../DynamicFormProvider";
import { EmptyPlaceholderText } from "../inlineForm/EmptyPlaceholderText";
import { ErrorMessage } from "../inlineForm/ErrorMessage";
import type { InlineInputState } from "../inlineForm/inputs/InlineInputState";
import { useInlineInputState } from "../inlineForm/inputs/InlineInputState";
import type { InputProps } from "../DynamicInput";
import { isAnswerRequestEmpty } from "./isAnswerRequestEmpty";

type DateProps = InputProps<string> & {
  inlineInputStateStyles?: Record<InlineInputState, SxProps<Theme>>;
};
export const DateInput: FC<DateProps> = ({ questionCard, onSave, readonly, inlineInputStateStyles }) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name: questionCard.id,
  });
  const { mandatoryQuestionErrorMessage, disableQuestionsByTag } = useDynamicFormState();
  const { inputState, setIsEditing } = useInlineInputState(readonly);
  const saveMutation = useMutation({
    mutationFn: (value: Dayjs | null) => {
      const formattedValue = value ? value.format("YYYY-MM-DD") : null;
      setValue(questionCard.id, formattedValue);
      return onSave(formattedValue);
    },
  });

  const disabled = Boolean(questionCard.tag && disableQuestionsByTag[questionCard.tag]);

  return (
    <FormControl fullWidth>
      <DatePicker
        disabled={readonly || disabled || saveMutation.isPending}
        hideStartAdornment={!field.value}
        disablePast
        format="DD MMMM YYYY"
        closeOnSelect
        open={inputState === "editing"}
        onClose={() => setIsEditing(false)}
        value={field.value ? dayjs(field.value) : null}
        onChange={value => saveMutation.mutate(value)}
        sx={{
          ...inlineInputStateStyles?.[inputState],
          ...(!field.value && { px: 2 }),
        }}
        slotProps={{
          textField: props => ({
            size: "small",
            id: questionCard.id,
            onFocus: e => e.target.blur(),
            onClick: () => !props.isFieldDisabled && setIsEditing(true),
            placeholder: t("DynamicForm.Inline.InputPlaceholder"),
            slotProps: {
              input: {
                startAdornment: !field.value ? <EmptyPlaceholderText /> : undefined,
                endAdornment: disabled && (
                  <Box sx={{ display: "flex", pr: 2 }}>
                    <LockIcon size={12} />
                  </Box>
                ),
              },
            },
          }),
          field: { clearable: true },
        }}
      />

      {error && <ErrorMessage errorMessage={error?.message} />}
      {!saveMutation.isPending && saveMutation.isError && <ErrorMessage />}
      {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(field.value) && (
        <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
      )}
    </FormControl>
  );
};
